import CustomTooltip from '../../shared/CustomTooltip';
import EditIcon from '@mui/icons-material/Edit';
import {
    Card,
    CardActionArea,
    Chip,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetBookingCapListQuery } from '../../../app/Slices/SevenDayApiSlice';
import { capitalizeName } from '../../../shared/utils';
import { CapSortKey } from './BookingCapToolbar';

export interface BookingCaps {
    id: number;
    name: string;
    yearlyCap: number;
    monthlyCap: number;
    weeklyCap: number;
    moduleTypes: string[]; // experience types
    modules: { id: number; name: string }[];
    visitType: string; // personal/corporate (kept if you still need to show it)
    guests: { id: number; name: string }[];
    excludedUsers: any[];
    appliesTo: string; // user/company
}

export default function BookingCapList({
    query,
    experienceType, // <- renamed
    appliesTo,
    sortKey,
}: {
    query: string;
    experienceType: string; // 'All' | boat | vehicle | activity | commercial | property
    appliesTo: string; // 'All' | user | company
    sortKey: CapSortKey;
}) {
    const navigate = useNavigate();
    const { data: getBookingCaps } = useGetBookingCapListQuery('/booking-caps');
    const bookingCaps: BookingCaps[] = getBookingCaps ?? [];

    const rows = useMemo(() => {
        const q = query.trim().toLowerCase();

        let r = bookingCaps.filter((c) => {
            const matchesQ =
                !q ||
                c.name.toLowerCase().includes(q) ||
                c.appliesTo?.toLowerCase().includes(q) ||
                (c.moduleTypes ?? []).some((m) => m.toLowerCase().includes(q));

            // Experience type => filter on moduleTypes
            const matchesExp =
                experienceType === 'All' ||
                (c.moduleTypes?.length ?? 0) === 0 || // treat empty as "applies to all"
                c.moduleTypes?.some(
                    (m) => m.toLowerCase() === experienceType.toLowerCase(),
                );

            const matchesApplies =
                appliesTo === 'All' ||
                c.appliesTo?.toLowerCase() === appliesTo.toLowerCase();

            return matchesQ && matchesExp && matchesApplies;
        });

        r = r.sort((a, b) => {
            if (sortKey === 'nameAsc') return a.name.localeCompare(b.name);
            if (sortKey === 'nameDesc') return b.name.localeCompare(a.name);
            if (sortKey === 'capHigh')
                return (b.yearlyCap ?? 0) - (a.yearlyCap ?? 0);
            if (sortKey === 'capLow')
                return (a.yearlyCap ?? 0) - (b.yearlyCap ?? 0);
            return 0;
        });

        return r;
    }, [bookingCaps, query, experienceType, appliesTo, sortKey]);

    if (rows.length === 0) {
        return (
            <Typography sx={{ mt: 3 }}>
                No booking caps. Try adjusting your filters.
            </Typography>
        );
    }

    return (
        <Stack spacing={1.5} sx={{ width: '100%' }}>
            {rows.map((cap) => (
                <CapRow
                    key={cap.id}
                    cap={cap}
                    onClick={() => navigate(`/bookingCap/${cap.id}/edit`)}
                />
            ))}
        </Stack>
    );
}

function CapRow({ cap, onClick }: { cap: BookingCaps; onClick: () => void }) {
    return (
        <Card
            variant='outlined'
            sx={{
                borderRadius: 2,
                '&:hover': { borderColor: 'primary.light', boxShadow: 1 },
            }}
            onClick={onClick}
        >
            <CardActionArea sx={{ px: 1.5, py: 1.5 }}>
                <Stack
                    direction='row'
                    alignItems='center'
                    spacing={2}
                    sx={{ width: '100%' }}
                >
                    {/* Left: name + cap summary */}
                    <Stack sx={{ minWidth: 220 }}>
                        <Typography variant='caption' sx={{ color: 'primary' }}>
                            {capitalizeName(cap?.name)}
                        </Typography>
                        {cap?.yearlyCap && (
                            <Typography variant='body3' color='text.primary'>
                                {cap.yearlyCap ?? 0} bookings per year/{' '}
                                {cap.appliesTo || '-'}
                            </Typography>
                        )}
                        {cap?.monthlyCap && (
                            <Typography variant='body3' color='text.primary'>
                                {cap.monthlyCap ?? 0} bookings per month/{' '}
                                {cap.appliesTo || '-'}
                            </Typography>
                        )}
                        {cap?.weeklyCap && (
                            <Typography variant='body3' color='text.primary'>
                                {cap.weeklyCap ?? 0} bookings per week/{' '}
                                {cap.appliesTo || '-'}
                            </Typography>
                        )}
                    </Stack>

                    {/* Middle meta */}
                    <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{ inWidth: 150, overflow: 'hidden' }}
                    >
                        {/* show first experience type as a chip (optional) */}
                        {cap.moduleTypes?.[0] && (
                            <Chip
                                size='small'
                                label={capitalizeName(cap.moduleTypes[0])}
                                variant='outlined'
                                sx={{
                                    minWidth: 150, // adjust to whatever looks balanced
                                    justifyContent: 'center', // keep text centered inside
                                }}
                            />
                        )}
                    </Stack>
                    <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{ overflow: 'hidden' }}
                    >
                        <Typography variant='body3' color='text.primary' noWrap>
                            Booking Cap #{cap.id} {''}|
                        </Typography>
                    </Stack>
                    <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
                    >
                        <Typography variant='body3' color='text.primary' noWrap>
                            Applied to: {cap.modules?.length ?? 0} Experiences
                            {cap.guests?.length
                                ? ` • ${cap.guests.length} ${
                                      cap.appliesTo?.toLowerCase() === 'user'
                                          ? cap.guests.length === 1
                                              ? 'user restricted'
                                              : 'users restricted'
                                          : cap.guests.length === 1
                                            ? 'company restricted'
                                            : 'companies restricted'
                                  }`
                                : ''}
                            {cap.excludedUsers?.length
                                ? ` • ${cap.excludedUsers.length} excluded ${
                                      cap.excludedUsers.length === 1
                                          ? 'user from restrictions'
                                          : 'users from restrictions'
                                  }`
                                : ''}
                        </Typography>
                    </Stack>

                    {/* Right actions */}
                    <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        onClick={(e) => e.stopPropagation()}
                    >
                        <CustomTooltip title='Edit'>
                            <IconButton size='small' onClick={onClick}>
                                <EditIcon fontSize='small' />
                            </IconButton>
                        </CustomTooltip>
                    </Stack>
                </Stack>
            </CardActionArea>
        </Card>
    );
}
